import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, AlertTriangle } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorHandler";
import pfLogo from "@/assets/pf-logo.png";

// SEC-002 (Aug 2026): Public self-registration is disabled. The Register
// tab has been removed from this page and `POST /api/auth/register` returns
// 403 for every non-bootstrap request.

const PF_BLUE = "#1F2A7C";
const PF_GREY = "#4D4D4D";

export default function Login() {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [kickedMessage, setKickedMessage] = useState(null);

  useEffect(() => {
    const msg = localStorage.getItem("session_kicked");
    if (msg) {
      setKickedMessage(msg);
      localStorage.removeItem("session_kicked");
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await login(loginData);
      const responseData = res.data || res;
      const token = responseData.token;
      const user = responseData.user;

      if (!token) {
        toast.error("Login failed - no token received");
        setLoading(false);
        return;
      }
      loginUser(token, user);
      if (!localStorage.getItem("token")) {
        toast.error("Session storage failed. Check browser settings.");
        setLoading(false);
        return;
      }
      toast.success(`Welcome back, ${user?.name || "User"}!`);
      let redirectUrl = "/";
      if (user?.role === "production_user") redirectUrl = "/production";
      else if (user?.role === "dispatch_user") redirectUrl = "/";
      setTimeout(() => {
        setLoading(false);
        window.location.href = redirectUrl;
      }, 200);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        toast.error(extractErrorMessage(err, "Invalid email or password"));
      } else if (!err.response) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(extractErrorMessage(err, "Login failed"));
      }
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ backgroundColor: "#F5F6F8" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-[420px] flex flex-col items-center">
        {/* Compact branding block */}
        <img
          src={pfLogo}
          alt="Param Fabrics"
          draggable={false}
          className="select-none pointer-events-none"
          style={{
            height: 64,       // +15% (was 56)
            width: "auto",
            objectFit: "contain",
            display: "block",
          }}
        />
        <p
          className="mt-2 text-[11px] uppercase font-medium"
          style={{ color: PF_GREY, letterSpacing: "0.14em" }}
        >
          TREND&nbsp;&nbsp;|&nbsp;&nbsp;THRIVE&nbsp;&nbsp;|&nbsp;&nbsp;TRUST
        </p>

        {/* ParamOS wordmark — sits 1.4 inch (≈134px) below the tagline */}
        <h1
          className="font-medium"
          style={{
            fontFamily:
              "'Playfair Display', 'DM Serif Display', Georgia, serif",
            letterSpacing: "-0.035em",
            lineHeight: 1,
            fontSize: "30px",
            marginTop: "1.4in",
          }}
        >
          <span style={{ color: PF_BLUE }}>Param</span>
          <span style={{ color: PF_GREY, fontSize: "1.12em" }}>OS</span>
        </h1>
        <p
          className="mt-1.5 text-[13px] sm:text-[14px]"
          style={{ color: PF_GREY }}
        >
          Built for Growth. Driven by Intelligence.
        </p>

        {/* Session-kicked banner */}
        {kickedMessage && (
          <div
            className="w-full mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"
            data-testid="session-kicked-msg"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">{kickedMessage}</p>
          </div>
        )}

        {/* Compact login card */}
        <form
          onSubmit={handleLogin}
          className="w-full bg-white mt-6 px-6 py-6 space-y-4"
          style={{
            borderRadius: "14px",
            border: "1px solid #EEF0F3",
            boxShadow:
              "0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 20px rgba(15, 23, 42, 0.05)",
          }}
        >
          <div>
            <Label
              htmlFor="login-email"
              className="text-[13px] font-semibold"
              style={{ color: "#111827" }}
            >
              Login ID
            </Label>
            <Input
              id="login-email"
              type="email"
              value={loginData.email}
              onChange={(e) =>
                setLoginData({ ...loginData, email: e.target.value })
              }
              placeholder=""
              className="mt-1.5 h-11 text-sm border-slate-200 focus-visible:ring-1 focus-visible:ring-slate-400"
              style={{ borderRadius: "8px" }}
              data-testid="login-email"
              autoComplete="email"
            />
          </div>

          <div>
            <Label
              htmlFor="login-password"
              className="text-[13px] font-semibold"
              style={{ color: "#111827" }}
            >
              Password
            </Label>
            <Input
              id="login-password"
              type="password"
              value={loginData.password}
              onChange={(e) =>
                setLoginData({ ...loginData, password: e.target.value })
              }
              placeholder=""
              className="mt-1.5 h-11 text-sm border-slate-200 focus-visible:ring-1 focus-visible:ring-slate-400"
              style={{ borderRadius: "8px" }}
              data-testid="login-password"
              autoComplete="current-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full text-white hover:opacity-95 transition-opacity"
            style={{
              backgroundColor: PF_GREY,
              borderRadius: "8px",
              height: "46px",
            }}
            data-testid="login-btn"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <p
          className="text-center mt-4"
          style={{ color: "#6B7280", fontSize: "13px" }}
        >
          Designed for Modern Textile Business
        </p>

        <p
          className="text-center"
          style={{
            color: "#000000",
            fontSize: "12px",
            marginTop: "0.75in",
          }}
        >
          Copyright © PARAM FABRICS , All rights reserved.
        </p>
      </div>
    </div>
  );
}
